import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Page,
  Card,
  TextField,
  Button,
  Banner,
  BlockStack,
  Text,
  InlineStack,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';

export default function ClinicReturnRequest() {
  const [rentalId, setRentalId] = useState('');
  const [notes, setNotes] = useState('');
  const [successVisible, setSuccessVisible] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      clinicPortalApi.createReturnRequest({
        rentalId: rentalId.trim(),
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      setSuccessVisible(true);
      setRentalId('');
      setNotes('');
    },
  });

  const handleSubmit = useCallback(() => {
    if (!rentalId.trim()) return;
    setSuccessVisible(false);
    mutation.mutate();
  }, [rentalId, mutation]);

  const handleDismissSuccess = useCallback(() => {
    setSuccessVisible(false);
  }, []);

  const rentalIdError =
    mutation.isError && !rentalId.trim() ? 'Rental ID is required' : undefined;

  return (
    <Page title="Request Return">
      <Card>
        <BlockStack gap="400">
          <Text as="p" variant="bodyMd" tone="subdued">
            Submit a return request for an active rental. Our team will contact you to arrange pickup.
          </Text>

          {successVisible && (
            <Banner
              title="Return request submitted"
              tone="success"
              onDismiss={handleDismissSuccess}
            >
              <p>Your return request has been received. We will be in touch shortly to arrange the return.</p>
            </Banner>
          )}

          {mutation.isError && (
            <Banner
              title="Submission failed"
              tone="critical"
              onDismiss={() => mutation.reset()}
            >
              <p>There was an error submitting your request. Please try again.</p>
            </Banner>
          )}

          <TextField
            label="Rental / Order ID"
            value={rentalId}
            onChange={setRentalId}
            placeholder="Enter the rental or order ID"
            autoComplete="off"
            error={rentalIdError}
          />

          <TextField
            label="Notes"
            value={notes}
            onChange={setNotes}
            multiline={4}
            placeholder="Optional: describe the reason for return or any special instructions..."
            autoComplete="off"
          />

          <InlineStack align="start">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={mutation.isPending}
              disabled={!rentalId.trim()}
            >
              Submit Return Request
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>
    </Page>
  );
}
